import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="job-detail-nav"
export default class extends Controller {
  static targets = ["navItem", "recruitmentGuideline", "companyInfo", "guidelineIcon", "companyIcon"];
  static values = {
    offset: { type: Number, default: 47 },
  };

  declare readonly navItemTargets: HTMLElement[];
  declare readonly recruitmentGuidelineTarget: HTMLElement;
  declare readonly companyInfoTarget: HTMLElement;
  declare readonly guidelineIconTarget: HTMLElement;
  declare readonly companyIconTarget: HTMLElement;
  declare readonly offsetValue: number;

  declare readonly hasRecruitmentGuidelineTarget: boolean;
  declare readonly hasCompanyInfoTarget: boolean;

  private handleScrollBound: () => void;
  private sections: HTMLElement[] = [];

  connect() {
    // スクロールハンドラー関数をバインド（removeEventListenerで同じ関数参照が必要）
    this.handleScrollBound = this.onScroll.bind(this);

    // セクションの配列を設定
    if (this.hasRecruitmentGuidelineTarget && this.hasCompanyInfoTarget) {
      this.sections = [this.recruitmentGuidelineTarget, this.companyInfoTarget];
    }

    // スクロールイベントの登録
    window.addEventListener("scroll", this.handleScrollBound);

    // スムーススクロールの設定
    this.setupSmoothScrollLinks();

    // 初期状態で一度実行
    this.onScroll();
  }

  disconnect() {
    // イベントリスナーの削除
    window.removeEventListener("scroll", this.handleScrollBound);
  }

  // 特定のナビアイテムを取得するヘルパーメソッド
  getNavItemForSection(sectionId: string): HTMLElement | undefined {
    return this.navItemTargets.find((item) => {
      const link = item.querySelector("a");
      return link && link.getAttribute("href") === `#${sectionId}`;
    });
  }

  onScroll() {
    if (this.sections.length === 0) return;

    let currentSection: HTMLElement | null = null;

    // 各セクションの位置を取得し、現在のスクロール位置に基づいて現在のセクションを特定
    this.sections.forEach((section) => {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const sectionHeight = section.getBoundingClientRect().height;

      if (window.scrollY >= sectionTop - 100 && window.scrollY < sectionTop + sectionHeight) {
        currentSection = section;
      }
    });

    // 全ての.nav_itemのis_activeを一旦リセット
    this.navItemTargets.forEach((item) => {
      item.classList.remove("is_active");
    });

    // id="company_info"よりも下にスクロールしている場合は「企業情報」をアクティブに
    if (this.hasCompanyInfoTarget) {
      const companyInfoNavItem = this.getNavItemForSection("company_info");
      if (
        companyInfoNavItem &&
        window.scrollY >= this.companyInfoTarget.offsetTop + this.companyInfoTarget.offsetHeight - 100
      ) {
        companyInfoNavItem.classList.add("is_active");
        return; // 他の処理をスキップ
      }
    }

    // 対応するセクションがある場合、その.nav_itemに`is_active`を付与
    if (currentSection) {
      const sectionId = (currentSection as HTMLElement).id;
      const navItem = this.getNavItemForSection(sectionId);
      if (navItem) {
        navItem.classList.add("is_active");
      }
    }
  }

  setupSmoothScrollLinks() {
    // ナビゲーションリンクにクリックイベントを設定
    this.navItemTargets.forEach((item) => {
      const link = item.querySelector("a");
      if (link) {
        link.addEventListener("click", this.handleLinkClick.bind(this));
      }
    });
  }

  handleLinkClick(event: Event) {
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement;
    const targetId = link.getAttribute("href")?.replace("#", "");
    const targetElement = document.getElementById(targetId || "");

    if (targetElement) {
      // 一時的に CSS のスムーススクロールを無効化
      document.documentElement.style.scrollBehavior = "auto";

      // 未ログインバー表示時（SP）は、バー＋ページ内ナビの2段スティッキーになるため
      // バーの高さ分オフセットを加算する
      const visitorInfoBar = document.querySelector<HTMLElement>(".visitor_info_bar");
      const barOffset = visitorInfoBar && window.innerWidth <= 768 ? visitorInfoBar.offsetHeight : 0;

      // 通常スクロールで移動
      window.scrollTo({
        top: targetElement.offsetTop - this.offsetValue - barOffset, // 固定ボタンの高さ＋未ログインバー分のオフセット考慮
        behavior: "auto", // スムーススクロール無効
      });

      // 次のフレームで CSS のスムーススクロールを元に戻す
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = "";
      });
    }
  }
}
